/**
 * The orthophoto, with whatever elevation models the report was bound to over it, and under it
 * what the CHM's bare ground says about its heights where a `chm_quality` input reaches the
 * viewer (staff, as a rule: a client's report does not carry one they may not see). The
 * photogrammetry's GSD sits under the map when the orthophoto's run measured it; an orthophoto
 * without one (older, or uploaded) shows nothing rather than its pixel size, which is the chosen
 * output resolution, not the GSD.
 */
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { MapView } from '@/map/MapView';
import { byKind, type ObjectKind } from '@/map/layers';
import { MapFrame } from './MapFrame';
import { ChmQualitySection } from './ortho/ChmQualitySection';
import { useChmQuality } from './ortho/chmQuality';
import type { KindReportProps } from './ReportPage';

const TOP_FIRST: ObjectKind[] = ['dsm', 'dtm', 'chm', 'orthophoto'];

export function OrthophotoReport({ refs, inputs = [], linkSource, options, onOptionsChange, tokenSource }: KindReportProps) {
  // An optional input that was never bound, or whose object is gone, is simply not here.
  const own = byKind(refs, TOP_FIRST);
  const qualityObject = inputs.find((i) => i.role === 'chm_quality' && i.available)?.object;
  const quality = useChmQuality(qualityObject, linkSource);
  const gsdCm = inputs.find((i) => i.role === 'ortho' && i.available)?.gsdCm ?? null;
  return (
    <Stack gap="xl">
      <MapFrame testId="report-orthophoto" wide={!!options.wide} onWideChange={(wide) => onOptionsChange({ ...options, wide })}>
        <MapView refs={own} options={options} onOptionsChange={onOptionsChange} tokenSource={tokenSource} />
      </MapFrame>
      {gsdCm !== null && <PhotogrammetryFacts gsdCm={gsdCm} />}
      {quality && <ChmQualitySection quality={quality} />}
    </Stack>
  );
}

function PhotogrammetryFacts({ gsdCm }: { gsdCm: number }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  return (
    <Text data-testid="ortho-gsd" fz="sm" c={brand.textMuted} title={t('orthophoto.gsdHint')}>
      {t('orthophoto.gsd', { value: number(gsdCm, 1) })}
    </Text>
  );
}
